/**
 * Copy trading. Money set aside to follow someone is a sub-account of its own inside your brokerage
 * account: its own cash, its own positions, its own averages — never mixed with what you hold of the
 * same stock. On the portfolio it is one row among your holdings; pressed, it opens underneath; it has
 * a page of its own. You start it yourself, with your own limits, and you can stop it at any time.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { create } from 'zustand'
import { ChevronRight } from 'lucide-react'
import { COPY_LIMIT_START, COPY_MIN_START, DEFAULT_COPY_SETTINGS, type CopyDetail, type CopyOverview, type CopyPoint, type CopyRemoteState, type CopySettings, type CopyView } from '@shared/copy'
import { copyApi, subscribeUserEvents } from './api'
import { Chart } from './Chart'
import { Btn, Dialog, Field, Status, Switch, askSure, cx, inputCls, menuOn, pct, toneOf, usd, type PopEntry } from './kit'
import { Page, ask } from './Page'
import { useDemo } from './store'
import { tradeFromCopy } from './Ticket'
import { starts } from './words'

const label = 'm-0 text-[12px] font-normal leading-4 tracking-normal text-ws-label'
const num = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 6 })
const day = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
const when = (ms: number) => new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
const gainText = (n: number, p: number) => `${n >= 0 ? '+' : '−'}${usd(Math.abs(n))} (${pct(p)})`
const STATE: Record<CopyView['status'], { tone: 'free' | 'off' | 'warn'; text: string }> = { active: { tone: 'free', text: 'Copying' }, paused: { tone: 'off', text: 'Paused' }, stopping: { tone: 'warn', text: 'Stopping' }, stopped: { tone: 'off', text: 'Stopped' } }

/** A person (not an agent): an initial in a circle. */
export const Person = ({ name, size = 24 }: { name: string; size?: number }) => (
  <span className="flex shrink-0 items-center justify-center rounded-full bg-ws-raise font-medium leading-none text-ws-fg-2" style={{ width: size, height: size, fontSize: size * 0.46 }} aria-hidden>
    {name.slice(0, 1).toLocaleUpperCase('tr')}
  </span>
)

/** A delay, as it is said: "0.4 s", "12 s", "3 min". */
export const lagWords = (ms: number) => (ms < 10_000 ? `${(ms / 1000).toFixed(1)} s` : ms < 120_000 ? `${Math.round(ms / 1000)} s` : `${Math.round(ms / 60_000)} min`)
/** How the link to a person's own server stands, in a few words. */
export function linkWords(l: CopyRemoteState['leaders'][number]): string {
  if (l.status === 'gone') return 'link removed on their side'
  if (!l.connected) return `link down${l.seenAt ? ` since ${new Date(l.seenAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}`
  return `${l.status === 'off' ? 'taken back: sales only · ' : ''}link ${l.rttMs != null ? `${l.rttMs} ms` : 'up'}`
}

const REFRESH_MS = 15_000
/** The least a copy starts with and keeps inside, as it is written. */
const MIN = `$${COPY_MIN_START}`

/** What the server knows about copying, kept fresh: read on mount, and again whenever it says something changed. */
export function useCopies() {
  const [data, setData] = useState<CopyOverview | null>(null)
  const load = useCallback(() => void copyApi.overview().then(setData, () => setData({ live: false, free: null, leaders: [], copies: [], followers: [] })), [])
  useEffect(() => {
    load()
    const off = subscribeUserEvents((e) => (e as { type: string }).type === 'copy' && load())
    // What a copy is worth moves with prices, not only with trades: looked at again while the page is in view.
    const t = window.setInterval(() => document.visibilityState === 'visible' && load(), REFRESH_MS)
    return () => (off(), window.clearInterval(t))
  }, [load])
  return { data, load }
}

const act = (job: Promise<unknown>, done: () => void, ok?: string) => {
  const s = useDemo.getState()
  void job.then(
    () => (ok && s.say(ok), done()),
    (e) => s.say(e instanceof Error ? e.message : 'That did not work'),
  )
}
const amountOf = (v: string) => Number(v.replace(/[^\d.]/g, ''))

/* ── what you can do with a copy ─────────────────────────────────────── */

/** Adding money to a copy, or taking some out: asked for in a window that shows what there is to move. */
const useMoney = create<{ ask: { c: CopyView; mode: 'add' | 'take'; done: () => void } | null }>(() => ({ ask: null }))
export const addMoney = (c: CopyView, done: () => void) => useMoney.setState({ ask: { c, mode: 'add', done } })
export const takeOut = (c: CopyView, done: () => void) => useMoney.setState({ ask: { c, mode: 'take', done } })

/** A sum of money typed, with quick shares of what there is to choose from. */
function Amount({ value, onChange, of, label: aria }: { value: string; onChange: (v: string) => void; of: number | null; label: string }) {
  return (
    <div>
      <label className={cx(inputCls, 'flex items-center')}>
        <span className="text-ws-fg-4">$</span>
        <input value={value} onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="0.00" className="h-full min-w-0 flex-1 pl-1 tabular-nums" autoFocus aria-label={aria} />
      </label>
      {of != null && of >= 1 && (
        <div className="mt-2 flex gap-1">
          {([25, 50, 75, 100] as const).map((p) => (
            <button key={p} type="button" onClick={() => onChange((Math.floor(of * p) / 100).toFixed(2))} className="h-7 rounded-[6px] bg-ws-raise px-[10px] text-[12px] tracking-normal text-ws-fg-2 transition-colors duration-150 hover:text-ws-fg">
              {p === 100 ? 'All' : `${p}%`}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
const Facts = ({ rows }: { rows: [string, string, string?][] }) => (
  <dl className="divide-y divide-ws-hair rounded-[8px] border border-ws-line px-3 text-[13px] tracking-normal">
    {rows.map(([k, v, sub]) => (
      <div key={k} className="flex min-h-9 items-center justify-between gap-3 py-[6px]">
        <dt className="min-w-0 text-ws-fg-3">
          {k}
          {sub && <span className="block text-[12px] leading-4 text-ws-fg-4">{sub}</span>}
        </dt>
        <dd className="m-0 shrink-0 tabular-nums text-ws-fg">{v}</dd>
      </div>
    ))}
  </dl>
)

/** The window for adding to a copy or taking out of it. One of these sits at the top of the screen tree. */
export function CopyMoneyHost() {
  const ask = useMoney((s) => s.ask)
  const [amount, setAmount] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState('')
  useEffect(() => (setAmount(''), setProblem(''), setBusy(false)), [ask])
  if (!ask) return null
  const { c, mode } = ask
  const close = () => useMoney.setState({ ask: null })
  const n = amountOf(amount)
  const add = mode === 'add'
  // a running copy keeps enough inside to follow small trades: everything comes out by stopping it
  const room = add ? c.free : Math.max(0, Math.min(c.cash, Math.floor((c.value - COPY_MIN_START) * 100) / 100))
  const shares = c.value - c.cash
  const ok = n >= (add ? 10 : 0.01) && (room == null || n <= room + 0.005)
  const go = async () => {
    setBusy(true)
    setProblem('')
    try {
      await (add ? copyApi.deposit(c.id, n) : copyApi.withdraw(c.id, n))
      useDemo.getState().say(add ? `${usd(n)} added — it is used for the next trades` : `${usd(n)} taken out — it is yours to use again`)
      ask.done()
      close()
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'That did not work')
      setBusy(false)
    }
  }
  const account = c.broker === 'ibkr' ? 'Interactive Brokers' : 'Robinhood'
  return (
    <Dialog
      open
      onClose={close}
      title={add ? `Add money to Copy · ${c.leader.name}` : `Take money out of Copy · ${c.leader.name}`}
      footer={
        <>
          <Btn variant="outline" onPanel onClick={close}>
            Cancel
          </Btn>
          <Btn variant="white" disabled={!ok || busy} onClick={() => void go()}>
            {busy ? 'Working…' : add ? `Add ${n ? usd(n) : ''}`.trim() : `Take out ${n ? usd(n) : ''}`.trim()}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Facts
          rows={
            add
              ? [
                  [`Free in your ${account} account`, c.free == null ? 'not known just now' : usd(Math.max(0, c.free)), 'Your available cash, less what your copies already hold'],
                  ['Cash inside this copy', usd(c.cash), 'Waits for the next trade'],
                  ['This copy is worth', usd(c.value)],
                ]
              : [
                  ['Cash inside this copy', usd(c.cash)],
                  ['Held in shares', usd(Math.max(0, shares)), shares > 0.005 ? 'To take this out, sell from the copy first' : undefined],
                  ['This copy is worth', usd(c.value)],
                  ['Can be taken out now', usd(room ?? 0), `A running copy keeps ${MIN} inside; stop it to take out everything`],
                ]
          }
        />
        <Field label={add ? 'How much to add' : 'How much to take out'} hint={add ? 'At least $10' : undefined}>
          <Amount value={amount} onChange={setAmount} of={room} label={add ? 'How much to add' : 'How much to take out'} />
        </Field>
        {n > 0 && ok && (
          <p className="text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
            {add
              ? `After this: ${usd(c.cash + n)} cash inside the copy (worth ${usd(c.value + n)})${c.free != null ? `, and ${usd(Math.max(0, c.free - n))} free in your account` : ''}. A buy of theirs then takes its share of the larger sum.`
              : `After this: ${usd(Math.max(0, c.cash - n))} cash inside the copy (worth ${usd(Math.max(0, c.value - n))}). The ${usd(n)} is free in your account again; no share is sold.`}
          </p>
        )}
        {n > 0 && !ok && room != null && n > room + 0.005 && <p className="text-[12px] leading-[18px] tracking-normal text-ws-down">{add ? `Only ${usd(Math.max(0, room))} is free in your account.` : `Only ${usd(room)} can be taken out now.`}</p>}
        {problem && <p className="text-[12px] leading-[18px] tracking-normal text-ws-down">{problem}</p>}
      </div>
    </Dialog>
  )
}
const stopCopying = (c: CopyView, done: () => void) =>
  askSure({
    title: `Stop copying ${c.leader.name}?`,
    body: c.positions.length ? (c.live ? 'Its shares are sold at the market and the money is free again. To keep the shares as your own instead, change "When it stops" in the copy’s settings first.' : 'The server is in rehearsal: nothing is sold, the shares stay in your account as your own.') : 'It holds no shares; its cash is simply free again.',
    cta: c.settings.onStop === 'keep' ? 'Stop and keep the shares' : 'Stop and sell',
    done: () => act(copyApi.stop(c.id, c.settings.onStop), done, 'The copy is stopping'),
  })
export const sellOne = (c: CopyView, symbol: string, done: () => void) =>
  askSure({
    title: `Sell ${symbol} out of this copy?`,
    body: `Only the copy’s own shares are sold, at the market; the money stays in the copy. Your own ${symbol} is not touched. The copy then leaves ${symbol} alone while ${c.leader.name} still holds theirs.`,
    cta: `Sell ${symbol}`,
    done: () => act(copyApi.sell(c.id, symbol, 1), done, 'The sale was sent'),
  })

const copyAgain = (c: CopyView, symbol: string, done: () => void) => act(copyApi.copyAgain(c.id, symbol), done, `${symbol} is copied again from ${c.leader.name}’s next buy of it`)

/** What a right-click on a copy offers. */
export function copyMenu(c: CopyView, done: () => void): PopEntry[] {
  const over = c.status === 'stopped'
  return [
    { label: 'Open', onSelect: () => useDemo.getState().openCopy(c.id) },
    ...(over
      ? []
      : ([
          'rule',
          { label: 'Add money', onSelect: () => addMoney(c, done) },
          { label: 'Take out', disabled: c.cash < 0.01, onSelect: () => takeOut(c, done) },
          ...(c.status === 'stopping' ? [] : [{ label: c.status === 'paused' ? 'Resume' : 'Pause', onSelect: () => act(c.status === 'paused' ? copyApi.resume(c.id) : copyApi.pause(c.id), done) }]),
          'rule',
          { label: 'Stop copying', danger: true, disabled: c.status === 'stopping', onSelect: () => stopCopying(c, done) },
        ] satisfies PopEntry[])),
  ]
}
/** What a right-click on a stock inside a copy offers: the copy's own shares of it, never yours. */
export function copyStockMenu(c: CopyView, symbol: string, done: () => void): PopEntry[] {
  const s = useDemo.getState()
  const live = c.status === 'active' || c.status === 'paused'
  return [
    { label: 'Trade', hint: 'from the copy', disabled: !live, onSelect: () => tradeFromCopy(c.id, symbol) },
    'rule',
    { label: 'Open chart', onSelect: () => (live && tradeFromCopy(c.id, symbol), s.openStock(symbol)) },
    { label: `Ask about ${symbol}`, onSelect: () => ask(null, starts.aboutStock(symbol)) },
    { label: 'Copy symbol', onSelect: () => (void navigator.clipboard?.writeText(symbol).catch(() => undefined), s.say(`${symbol} copied`)) },
    'rule',
    { label: `Sell all ${symbol} out of the copy`, danger: true, disabled: !live || c.status === 'stopping', onSelect: () => sellOne(c, symbol, done) },
  ]
}

/** What a copy that holds no shares says: it never traded yet, or what it bought has been sold again. */
export const emptyText = (c: CopyView) => (c.trades > 0 ? `It holds no shares right now: what it bought was sold again. The next buy ${c.leader.name} makes is copied.` : `Nothing has been copied yet. The next trade ${c.leader.name} makes is the first.`)

/* ── on the portfolio: one row per copy, opening in place ────────────── */

export function CopyRows() {
  const { data, load } = useCopies()
  const openCopy = useDemo((s) => s.openCopy)
  const [open, setOpen] = useState<string | null>(null)
  const list = (data?.copies ?? []).filter((c) => c.status !== 'stopped')
  if (!list.length) return null
  return (
    <section>
      <h2 className={label}>
        Copies <span className="text-ws-fg-4">· money set aside to follow someone, each a sub-account of its own</span>
      </h2>
      <div className="mt-2 divide-y divide-ws-hair border-y border-ws-line">
        {list.map((c) => (
          <div key={c.id}>
            <button type="button" aria-expanded={open === c.id} onClick={() => setOpen(open === c.id ? null : c.id)} className="flex min-h-12 w-full items-center gap-3 py-2 text-left text-[13px] tracking-normal transition-colors duration-150 hover:bg-ws-hover">
              <ChevronRight size={13} strokeWidth={2} className={cx('shrink-0 text-ws-fg-4 transition-transform duration-150', open === c.id && 'rotate-90')} />
              <Person name={c.leader.name} />
              <span className="min-w-0 flex-1">
                <span className="font-medium text-ws-fg">{c.leader.name}</span>
                <span className="ml-2 text-ws-fg-3">
                  {c.positions.length} {c.positions.length === 1 ? 'stock' : 'stocks'} · since {day(c.since)}
                </span>
              </span>
              <Status tone={STATE[c.status].tone}>{c.live ? STATE[c.status].text : 'Rehearsal'}</Status>
              <span className="w-[104px] shrink-0 text-right tabular-nums text-ws-fg">{usd(c.value)}</span>
              <span className={cx('hidden w-[150px] shrink-0 text-right tabular-nums sm:block', toneOf(c.gain))}>{gainText(c.gain, c.gainPct)}</span>
            </button>
            {open === c.id && (
              <div className="pb-4 pl-[25px]">
                <CopyPositions c={c} onChanged={load} />
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Btn variant="outline" onClick={() => addMoney(c, load)}>
                    Add money
                  </Btn>
                  <Btn variant="outline" disabled={c.cash < 0.01} onClick={() => takeOut(c, load)}>
                    Take out
                  </Btn>
                  <Btn variant="outline" onClick={() => openCopy(c.id)}>
                    Open
                  </Btn>
                  <span className="ml-auto text-[12px] tracking-normal text-ws-fg-3">
                    {usd(c.putIn)} put in · {usd(c.cash)} cash
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

function CopyPositions({ c, onChanged }: { c: CopyView; onChanged: () => void }) {
  if (!c.positions.length) return <p className="m-0 py-3 text-[12px] tracking-normal text-ws-fg-3">{emptyText(c)}</p>
  return (
    <div className="divide-y divide-ws-hair">
      {c.positions.map((p) => (
        <div key={p.symbol} className="flex min-h-10 items-center gap-3 py-[6px] text-[13px] tracking-normal">
          <span className="w-[64px] shrink-0 font-medium text-ws-fg">{p.symbol}</span>
          <span className="min-w-0 flex-1 truncate text-ws-fg-3">
            {num(p.qty)} at {usd(p.avg)}
            {p.stale ? ' · no live price' : ''}
          </span>
          <span className="w-[96px] shrink-0 text-right tabular-nums text-ws-fg">{usd(p.value)}</span>
          <span className={cx('hidden w-[150px] shrink-0 text-right tabular-nums sm:block', toneOf(p.gain))}>{gainText(p.gain, p.gainPct)}</span>
          <button type="button" disabled={c.status === 'stopping'} onClick={() => sellOne(c, p.symbol, onChanged)} className="shrink-0 rounded-[6px] px-2 py-1 text-[12px] text-ws-fg-3 transition-colors hover:bg-ws-hover hover:text-ws-down disabled:opacity-40">
            Sell
          </button>
        </div>
      ))}
    </div>
  )
}

/* ── a copy's own page ───────────────────────────────────────────────── */

const Fig = ({ k, children, sub }: { k: string; children: ReactNode; sub?: ReactNode }) => (
  <div className="min-w-0 py-4 sm:px-5 sm:first:pl-0">
    <div className="text-[12px] tracking-normal text-ws-fg-3">{k}</div>
    <div className="mt-1 text-[20px] font-medium leading-7 tracking-[-0.01em] text-ws-fg tabular-nums">{children}</div>
    {sub && <div className="mt-1 text-[12px] tracking-normal text-ws-fg-3">{sub}</div>}
  </div>
)

/** The limits of a copy: the follower's own. Each can be switched off, and changed while the copy runs. */
function Limits({ value, onChange }: { value: CopySettings; onChange: (s: CopySettings) => void }) {
  const row = 'flex min-h-11 items-center gap-3 px-3 py-[6px]'
  const box = 'flex h-8 w-[84px] shrink-0 items-center rounded-[8px] border border-ws-line bg-ws-field px-[10px] text-[13px] tracking-normal focus-within:border-ws-focus'
  const Num = ({ v, min, max, on, aria }: { v: number; min: number; max: number; on: (n: number) => void; aria: string }) => (
    <label className={box}>
      <input defaultValue={v} key={v} onBlur={(e) => on(Math.max(min, Math.min(max, Number(e.target.value.replace(',', '.')) || v)))} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} inputMode="decimal" aria-label={aria} className="h-full min-w-0 flex-1 text-right tabular-nums" />
      <span className="pl-1 text-ws-fg-4">%</span>
    </label>
  )
  const words = (title: string, sub: string) => (
    <span className="min-w-0 flex-1">
      <span className="block text-[13px] leading-[18px] tracking-normal text-ws-fg">{title}</span>
      <span className="block text-[12px] leading-4 tracking-normal text-ws-fg-3">{sub}</span>
    </span>
  )
  const capped = value.maxPositionPct < 100
  return (
    <div className="divide-y divide-ws-hair rounded-[8px] border border-ws-line">
      <div className={row}>
        {words('Never chase a price', value.slippagePct == null ? 'Off: a buy goes at the price there is, whatever they paid' : 'A buy is skipped when your price is this far above what they paid for that trade')}
        {value.slippagePct != null && <Num v={value.slippagePct} min={0.1} max={5} aria="Slippage limit" on={(n) => onChange({ ...value, slippagePct: n })} />}
        <Switch on={value.slippagePct != null} onChange={(v) => onChange({ ...value, slippagePct: v ? COPY_LIMIT_START.slippagePct : null })} label="Never chase a price" />
      </div>
      <div className={row}>
        {words('Largest single stock', capped ? 'No stock grows past this share of the copy, even if they hold more of it' : 'Off: a stock takes the same share here as it takes of their account')}
        {capped && <Num v={value.maxPositionPct} min={5} max={99} aria="Largest single stock" on={(n) => onChange({ ...value, maxPositionPct: n })} />}
        <Switch on={capped} onChange={(v) => onChange({ ...value, maxPositionPct: v ? COPY_LIMIT_START.maxPositionPct : 100 })} label="Largest single stock" />
      </div>
      <div className={row}>
        {words('Stop by itself after a loss', value.stopAtPct == null ? 'Off: it runs until you stop it' : 'When the copy is down this much of the money put in')}
        {value.stopAtPct != null && <Num v={value.stopAtPct} min={1} max={95} aria="Stop after a loss of" on={(n) => onChange({ ...value, stopAtPct: n })} />}
        <Switch on={value.stopAtPct != null} onChange={(v) => onChange({ ...value, stopAtPct: v ? COPY_LIMIT_START.stopAtPct : null })} label="Stop by itself after a loss" />
      </div>
      <div className={row}>
        {words('When it stops', value.onStop === 'sell' ? 'Its shares are sold at the market' : 'Its shares stay in your account as your own')}
        <div className="flex h-8 shrink-0 gap-px rounded-[8px] border border-ws-line bg-ws-field p-[3px] text-[12px]">
          {(['sell', 'keep'] as const).map((m) => (
            <button key={m} type="button" onClick={() => onChange({ ...value, onStop: m })} className={cx('rounded-[5px] px-3 transition-colors duration-150', value.onStop === m ? 'bg-ws-raise text-ws-fg' : 'text-ws-fg-3 hover:text-ws-fg')}>
              {m === 'sell' ? 'Sell' : 'Keep'}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

const signedUsd = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd(Math.abs(n))}`
const RANGES = ['1D', '1W', '1M', '3M', 'ALL'] as const
type Range = (typeof RANGES)[number]
const SPAN: Record<Range, [number, string]> = { '1D': [86_400_000, 'Past day'], '1W': [7 * 86_400_000, 'Past week'], '1M': [30 * 86_400_000, 'Past month'], '3M': [91 * 86_400_000, 'Past 3 months'], ALL: [Infinity, 'Since it started'] }
const two = 'hidden text-right tabular-nums lg:block'
const lower = 'block text-[12px] leading-4'
const quietBtn = 'rounded-[6px] px-2 py-1 text-[12px] tracking-normal text-ws-fg-3 transition-colors duration-150 hover:bg-ws-raise hover:text-ws-fg'

/** A change, the way a broker says it: a small arrow, the amount, the percent, and what it is measured over. */
function Delta({ v, base, label: text }: { v: number; base: number; label: string }) {
  const up = v >= 0
  return (
    <div className={cx('flex h-5 items-center gap-[6px] text-[13px] tracking-normal tabular-nums', up ? 'text-ws-up' : 'text-ws-down')}>
      <svg width="9" height="8" viewBox="0 0 9 8" aria-hidden className={cx('shrink-0', !up && 'rotate-180')}>
        <path d="M4.5 0.6 8.6 7.4H0.4z" fill="currentColor" />
      </svg>
      <span className="font-medium">
        {usd(Math.abs(v))} ({Math.abs(base ? (v / base) * 100 : 0).toFixed(2)}%)
      </span>
      <span className="text-ws-fg-2">{text}</span>
    </div>
  )
}
const Block = ({ label: title, children }: { label: string; children: ReactNode }) => (
  <section>
    <div className="flex h-8 items-center border-b border-ws-edge text-ws-fg">{title}</div>
    {children}
  </section>
)

/**
 * One copy, as a portfolio of its own: what it is worth and the line of it, what it holds (each stock its
 * own row, opening that stock), what it did — and beside it the money, the limits and how it copies.
 */
export function CopyPage({ id }: { id: string }) {
  const [d, setD] = useState<CopyDetail | null>(null)
  const [line, setLine] = useState<CopyPoint[]>([])
  const [range, setRange] = useState<Range>('ALL')
  const [at, setAt] = useState<number | null>(null)
  const [gone, setGone] = useState(false)
  /** Copied from another server: how the link to it stands. */
  const [remote, setRemote] = useState<CopyRemoteState | null>(null)
  const openStock = useDemo((s) => s.openStock)
  const load = useCallback(() => {
    void copyApi.detail(id).then(setD, () => setGone(true))
    void copyApi.history(id).then(setLine, () => {})
    void copyApi.overview().then((o) => setRemote(o.remote ?? null), () => {})
  }, [id])
  useEffect(() => {
    load()
    const off = subscribeUserEvents((e) => (e as { type: string }).type === 'copy' && load())
    const t = window.setInterval(() => document.visibilityState === 'visible' && load(), REFRESH_MS)
    return () => (off(), window.clearInterval(t))
  }, [load])
  if (gone) return <div className="p-10 text-[12px] tracking-normal text-ws-fg-4">That copy is not there.</div>
  if (!d) return <div className="p-10 text-[12px] tracking-normal text-ws-fg-4">Loading…</div>
  const over = d.status === 'stopped'
  // The line: what the copy was worth, and beside it how much had been put in — money added is not a gain.
  const since = Date.now() - SPAN[range][0]
  const pts = line.filter((p) => p.at >= since)
  const shown = pts.length > 1 ? pts : line.slice(-2)
  const made = (p: CopyPoint) => p.value - p.putIn
  const cur = at != null && shown[at] ? shown[at] : null
  const value = cur?.value ?? d.value
  const moved = shown.length > 1 ? (cur ? made(cur) : d.value - d.putIn) - made(shown[0]) : 0
  const base = shown[0]?.putIn || d.putIn || 1
  const cols = 'lg:grid-cols-[minmax(150px,1.6fr)_repeat(6,minmax(64px,1fr))_52px_64px]'
  const th = 'hidden text-right lg:block'
  const row = (k: string, v: ReactNode, cls = 'text-ws-fg-2') => (
    <div key={k} className="flex h-9 items-center justify-between gap-3">
      <dt className="shrink-0 text-ws-fg-3">{k}</dt>
      <dd className={cx('m-0 truncate tabular-nums', cls)}>{v}</dd>
    </div>
  )
  const account = d.broker === 'ibkr' ? 'Interactive Brokers' : 'Robinhood'
  const link = remote?.leaders.find((l) => l.id === d.leader.id) ?? null
  // a stock of the copy opens with the ticket pointed at the copy's own shares of it
  const openIn = (symbol: string) => (!over && d.status !== 'stopping' && tradeFromCopy(d.id, symbol), openStock(symbol))
  const sitting = over ? [] : d.sitting.filter((s) => !d.positions.some((p) => p.symbol === s))
  return (
    <Page className="mx-auto max-w-[1360px]">
      <div className="grid items-start gap-x-8 gap-y-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0">
          <div>
            <div className="flex h-6 min-w-0 items-center gap-2 text-ws-fg-2">
              <span className="font-medium text-ws-fg">Copy · {d.leader.name}</span>
              <span className="truncate">inside your {account} account</span>
              <Status tone={STATE[d.status].tone} className="ml-1">
                {d.live || over ? STATE[d.status].text : 'Rehearsal'}
              </Status>
            </div>
            {link && !over && (!link.connected || link.status !== 'active') && (
              <p className="mt-2 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
                <span className="text-ws-warn">{link.status === 'gone' ? 'The link is gone.' : link.status === 'off' ? 'Taken back.' : 'The link is down.'}</span>{' '}
                {link.status === 'gone'
                  ? `It was removed on ${d.leader.name}’s server: this copy hears nothing any more, their sales neither. Stop it, or look after what it holds yourself.`
                  : link.status === 'off'
                    ? `${d.leader.name} took the link back: their new buys no longer reach this copy; their sales still do.`
                    : `${d.leader.name}’s server (${link.host}) cannot be reached: until it is back nothing they do reaches this copy, their sales neither. What was missed is made up for when it returns.`}
              </p>
            )}
            <div className="mt-1 text-[32px] font-medium leading-10 tracking-[-0.025em] tabular-nums text-ws-fg">{usd(value)}</div>
            <div className="mt-1 h-10">
              <Delta v={moved} base={base} label={cur ? `made by ${new Date(cur.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : SPAN[range][1]} />
              {/* over its whole life the first line is the whole gain: the second says what it is measured on */}
              {!cur && (range === 'ALL' ? <div className="flex h-5 items-center text-[13px] tracking-normal text-ws-fg-3">on {usd(d.putIn)} put in</div> : <Delta v={d.gain} base={d.deposited || d.putIn} label={`since it started, on ${usd(d.putIn)} put in`} />)}
            </div>
            <div className="mt-1">
              {shown.length > 1 ? (
                <Chart bare points={shown.map((p) => p.value)} labels={shown.map((p) => new Date(p.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }))} height={240} onScrub={setAt} live={d.status === 'active'} />
              ) : (
                <div className="flex h-[120px] items-center text-[12px] tracking-normal text-ws-fg-4">Its line appears as it runs: what the copy is worth is noted every few minutes and at every trade.</div>
              )}
            </div>
            <div className="ws-noscroll flex h-10 items-stretch gap-1 overflow-x-auto border-b border-ws-edge">
              {RANGES.map((r) => (
                <button key={r} type="button" aria-pressed={range === r} onClick={() => (setRange(r), setAt(null))} className={cx('relative shrink-0 px-[10px] text-[12px] font-medium tracking-normal transition-colors duration-150 first:-ml-[10px]', range === r ? (moved >= 0 ? 'text-ws-up' : 'text-ws-down') : 'text-ws-fg-2 hover:text-ws-fg')}>
                  {r}
                  {range === r && <span className={cx('absolute inset-x-[10px] -bottom-px h-[2px]', moved >= 0 ? 'bg-ws-up' : 'bg-ws-down')} />}
                </button>
              ))}
            </div>
          </div>
          {!d.live && !over && (
            <p className="m-0 mt-4 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
              <span className="text-ws-warn">Rehearsal.</span> This server works every copied trade out and writes down what it would have sent — and sends nothing. The lines below marked “would have sent” are those.
            </p>
          )}

          {!over && d.value < COPY_MIN_START && (
            <p className="m-0 mt-4 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
              <span className="text-ws-warn">Under {MIN} inside.</span> A trade that used less than {((1 / Math.max(d.value, 1)) * 100).toFixed(1)}% of {d.leader.name}’s account comes out under $1 here, which a broker does not take — it is skipped. Add money to follow those too.
            </p>
          )}

          <div className="-mx-3 mt-8">
            <div className="flex h-10 items-center border-b border-ws-edge px-3 text-ws-fg">
              Inside this copy <span className="ml-1 text-ws-fg-3">({d.positions.length})</span>
            </div>
            <div className={cx('hidden h-9 items-center gap-x-3 border-b border-ws-edge px-3 text-[12px] tracking-normal text-ws-fg-3 lg:grid', cols)}>
              <span>Symbol</span>
              <span className={th}>Qty</span>
              <span className={th}>Avg cost</span>
              <span className={th}>Last</span>
              <span className={th}>Today</span>
              <span className={th}>Value</span>
              <span className={th}>Open profit</span>
              <span className={th}>Share</span>
              <span />
            </div>
            <div className="divide-y divide-ws-edge border-b border-ws-edge">
              {d.positions.map((p) => (
                <div key={p.symbol} role="button" tabIndex={0} onClick={() => openIn(p.symbol)} onKeyDown={(e) => e.key === 'Enter' && openIn(p.symbol)} {...menuOn(() => copyStockMenu(d, p.symbol, load))} className={cx('grid h-[52px] cursor-pointer grid-cols-[1fr_auto] items-center gap-x-3 px-3 text-[13px] tracking-normal transition-colors duration-150 hover:bg-ws-hover', cols)}>
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-medium leading-[18px] tracking-[-0.01em] text-ws-fg">{p.symbol}</span>
                    <span className={cx(lower, 'truncate text-ws-fg-3')}>{p.name ?? (p.stale ? 'no live price' : 'bought by this copy')}</span>
                  </span>
                  <span className={cx(two, 'text-ws-fg-2')}>{num(p.qty)}</span>
                  <span className={cx(two, 'text-ws-fg-2')}>{p.avg.toFixed(2)}</span>
                  <span className={cx(two, 'text-ws-fg')}>{p.last.toFixed(2)}</span>
                  <span className={cx(two, toneOf(p.day))}>{signedUsd(p.day)}</span>
                  <span className={cx(two, 'text-ws-fg')}>{usd(p.value)}</span>
                  <span className={cx(two, toneOf(p.gain))}>
                    {signedUsd(p.gain)}
                    <span className={lower}>{pct(p.gainPct)}</span>
                  </span>
                  <span className={cx(two, 'text-ws-fg-3')}>{d.value ? ((p.value / d.value) * 100).toFixed(0) : 0}%</span>
                  <span className="flex justify-end">
                    <button type="button" disabled={over || d.status === 'stopping'} onClick={(e) => (e.stopPropagation(), sellOne(d, p.symbol, load))} className={cx(quietBtn, '-mr-2 disabled:opacity-40')}>
                      Sell
                    </button>
                  </span>
                </div>
              ))}
              {!d.positions.length && <div className="px-3 py-3 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">{emptyText(d)}</div>}
              {sitting.map((s) => (
                <div key={s} className="flex min-h-[52px] items-center gap-3 px-3 py-2 text-[13px] tracking-normal">
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-medium leading-[18px] tracking-[-0.01em] text-ws-fg-3">{s}</span>
                    <span className={cx(lower, 'text-ws-fg-3')}>
                      You sold it out of this copy. It is left alone while {d.leader.name} still holds theirs; a position they open anew is copied again.
                    </span>
                  </span>
                  <button type="button" onClick={() => copyAgain(d, s, load)} className={cx(quietBtn, '-mr-2 shrink-0')}>
                    Copy it again
                  </button>
                </div>
              ))}
              <div className={cx('grid h-11 grid-cols-[1fr_auto] items-center gap-x-3 px-3 text-[13px] tracking-normal', cols)}>
                <span className="text-ws-fg-2">Cash inside</span>
                <span className="text-right tabular-nums text-ws-fg lg:col-start-6">{usd(d.cash)}</span>
                <span className={cx(two, 'text-ws-fg-3 lg:col-start-8')}>{d.value ? ((d.cash / d.value) * 100).toFixed(0) : 0}%</span>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <div className="flex h-10 items-center border-b border-ws-edge text-ws-fg">What it did</div>
            <ol className="m-0 list-none divide-y divide-ws-edge p-0">
              {d.log.slice(0, 120).map((l, i) => (
                <li key={i} className="grid min-h-11 grid-cols-[1fr] items-center gap-x-3 py-2 text-[13px] leading-[19px] tracking-normal sm:grid-cols-[128px_1fr]">
                  <span className="text-[12px] text-ws-fg-3">{when(l.at)}</span>
                  <span className={l.kind === 'skipped' || l.kind === 'rehearsed' || l.kind === 'note' ? 'text-ws-fg-3' : l.kind === 'failed' ? 'text-ws-down' : 'text-ws-fg-2'}>{l.text}</span>
                </li>
              ))}
              {!d.log.length && <li className="py-3 text-[12px] tracking-normal text-ws-fg-3">Nothing yet.</li>}
            </ol>
          </div>
        </div>

        <div className="space-y-6 text-[13px] tracking-normal">
          <Block label="This copy">
            <dl className="m-0 divide-y divide-ws-edge">
              {row('Put in', d.withdrawn > 0 ? `${usd(d.putIn)} · ${usd(d.deposited)} in, ${usd(d.withdrawn)} out` : usd(d.putIn))}
              {row('Worth now', usd(d.value), 'text-ws-fg')}
              {row('Made', `${signedUsd(d.gain)} (${pct(d.gainPct)})`, toneOf(d.gain))}
              {row('Today', `${signedUsd(d.day)} (${pct(d.dayPct)})`, toneOf(d.day))}
              {row('Trades', `${d.trades}`)}
              {row('Copies', d.leader.name)}
              {row('Since', day(d.since))}
              {row('Account', account)}
              {link && row('Their server', link.host)}
              {link && !over && row('Link', link.connected && link.status === 'active' ? (link.rttMs != null ? `${link.rttMs} ms there and back` : 'up') : linkWords(link), link.connected && link.status === 'active' ? 'text-ws-fg-2' : 'text-ws-warn')}
              {link && link.lagMs != null && row('Last trade known here', `${lagWords(link.lagMs)} after it filled`)}
              {d.stopReason && row('Ended', d.stopReason)}
            </dl>
            {!over && (
              <div className="mt-3 flex flex-wrap gap-2">
                <Btn variant="outline" onClick={() => addMoney(d, load)}>
                  Add money
                </Btn>
                <Btn variant="outline" disabled={d.cash < 0.01} onClick={() => takeOut(d, load)}>
                  Take out
                </Btn>
                {d.status !== 'stopping' && (
                  <Btn variant="outline" onClick={() => act(d.status === 'paused' ? copyApi.resume(d.id) : copyApi.pause(d.id), load)}>
                    {d.status === 'paused' ? 'Resume' : 'Pause'}
                  </Btn>
                )}
              </div>
            )}
          </Block>
          {!over && (
            <Block label="Your limits">
              <div className="mt-3">
                <Limits value={d.settings} onChange={(s) => act(copyApi.settings(d.id, s), load)} />
              </div>
            </Block>
          )}
          <Block label="How it copies">
            <ul className="m-0 list-none divide-y divide-ws-edge p-0 text-ws-fg-2">
              {[
                `A buy takes the same share of this money as it took of ${d.leader.name}’s account.`,
                `A sale of theirs sells the same share of what this copy holds of that stock — never more than it holds, and nothing it does not hold.`,
                `Trades ${d.leader.name} made before you started are not copied.`,
                `A stock you sell out of the copy yourself is left alone while ${d.leader.name} still holds theirs; it is never bought back behind your back.`,
                d.settings.slippagePct != null ? `It never buys at a price more than ${d.settings.slippagePct}% above what they paid for that trade.` : 'It buys at the price there is when their trade is seen.',
                'Its shares are kept apart from your own shares of the same stock, and its money from the rest of your cash.',
                ...(link ? [`${d.leader.name} trades on their own server (${link.host}). It sends this one each order, sealed; your orders are placed here, on yours, and nothing about your money goes back.`, 'If their server cannot be reached you are told; what was missed is made up for when it returns, and a stock they no longer hold is sold here to match.'] : []),
                `${d.leader.name} is told nothing about your money.`,
              ].map((x) => (
                <li key={x} className="py-[9px] leading-[19px]">
                  {x}
                </li>
              ))}
            </ul>
            {!over && (
              <Btn variant="danger" className="mt-3" disabled={d.status === 'stopping'} onClick={() => stopCopying(d, load)}>
                Stop copying
              </Btn>
            )}
          </Block>
        </div>
      </div>
    </Page>
  )
}

/* ── starting one ────────────────────────────────────────────────────── */

export function StartCopy({ leader, free = null, onClose, onStarted }: { leader: { id: string; name: string } | null; /** What is free in the account to set aside. */ free?: number | null; onClose: () => void; onStarted: () => void }) {
  const [amount, setAmount] = useState('')
  const [settings, setSettings] = useState<CopySettings>(DEFAULT_COPY_SETTINGS)
  const [terms, setTerms] = useState<string | null>(null)
  const [agreed, setAgreed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState('')
  useEffect(() => {
    if (!leader) return
    setAmount('')
    setSettings(DEFAULT_COPY_SETTINGS)
    setAgreed(false)
    setProblem('')
    void copyApi.consent().then((c) => setTerms(c.text), () => setTerms(null))
  }, [leader?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const value = amountOf(amount)
  const ok = value >= COPY_MIN_START && agreed && !!terms && (free == null || value <= free + 0.005)
  const start = async () => {
    if (!leader || !ok) return
    setBusy(true)
    setProblem('')
    try {
      await copyApi.start({ leaderId: leader.id, amount: value, settings })
      useDemo.getState().say(`You are copying ${leader.name}`)
      onStarted()
      onClose()
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'It could not be started')
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog
      open={!!leader}
      onClose={onClose}
      title={leader ? `Copy ${leader.name}` : ''}
      width={560}
      footer={
        <>
          <Btn variant="outline" onPanel onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="white" disabled={!ok || busy} onClick={() => void start()}>
            {busy ? 'Starting…' : 'Start copying'}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Facts rows={[['Free in your account', free == null ? 'not known just now' : usd(Math.max(0, free)), 'Your available cash, less what your other copies hold']]} />
        <Field label="Money to set aside" hint={`At least ${MIN}`}>
          <Amount value={amount} onChange={setAmount} of={free} label="Money to set aside" />
        </Field>
        {value > 0 && value < COPY_MIN_START && <p className="text-[12px] leading-[18px] tracking-normal text-ws-down">A copy starts with at least {MIN}: with less, their smaller trades would come out under $1 here, which a broker does not take.</p>}
        {free != null && value > free + 0.005 && <p className="text-[12px] leading-[18px] tracking-normal text-ws-down">Only {usd(Math.max(0, free))} is free in your account.</p>}
        <div>
          <h3 className={cx(label, 'mb-[6px]')}>Your limits</h3>
          <Limits value={settings} onChange={setSettings} />
        </div>
        <div>
          <h3 className={cx(label, 'mb-[6px]')}>What you are agreeing to</h3>
          <div className="max-h-[180px] overflow-y-auto whitespace-pre-wrap rounded-[8px] border border-ws-line px-3 py-[10px] text-[12px] leading-[18px] tracking-normal text-ws-fg-2">{terms ?? 'Loading…'}</div>
          <label className="mt-3 flex cursor-pointer items-start gap-3 text-[13px] leading-5 tracking-normal text-ws-fg-2">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-[3px] h-4 w-4 shrink-0 accent-white" />
            I have read this. Starting this copy and its limits are my own decision.
          </label>
        </div>
        {problem && <p className="text-[12px] leading-[18px] tracking-normal text-ws-down">{problem}</p>}
      </div>
    </Dialog>
  )
}
